import { http } from '../lib/api/http';
import { createNotification } from './notificationsApi';
import { getSessionUser } from './sessionUser';
import { decodePublicationId, isNumericPublicationId } from '../utils/publicationId';

const FEED_REACTIONS_ENDPOINT = '/feed-reactions';
const FEED_POSTS_ENDPOINT = '/feed/posts';
const SERVICE_REQUESTS_ENDPOINT = '/service-requests';
const PUBLICATIONS_ENDPOINT = '/publications';

const getSessionUserSnapshot = async () => {
  try {
    return await getSessionUser();
  } catch {
    return null;
  }
};

const truncateText = (value, max = 60) => {
  if (!value) return '';
  const normalized = String(value).replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  return normalized.length > max ? `${normalized.slice(0, max - 1).trim()}...` : normalized;
};

const fetchReactionTargetMeta = async (kind, targetId) => {
  if (!targetId) return null;
  const safeId =
    kind === 'publication' && isNumericPublicationId(targetId)
      ? decodePublicationId(targetId)
      : targetId;
  const encodedId = encodeURIComponent(safeId);
  try {
    if (kind === 'post') {
      const { data } = await http.get(`${FEED_POSTS_ENDPOINT}/${encodedId}`);
      return {
        companyId:
          data?.company_id ||
          data?.companyId ||
          data?.company?.id ||
          data?.company ||
          '',
        ownerId:
          data?.created_by ||
          data?.createdBy ||
          data?.author?.id ||
          '',
        label: truncateText(data?.content || '', 80),
        status: data?.status || '',
      };
    }
    if (kind === 'request') {
      const { data } = await http.get(`${SERVICE_REQUESTS_ENDPOINT}/${encodedId}`);
      return {
        companyId: data?.company_id || data?.companyId || '',
        ownerId: data?.created_by || data?.createdBy || '',
        label: truncateText(data?.title || '', 80),
        status: data?.status || '',
      };
    }
    if (kind === 'publication') {
      const { data } = await http.get(`${PUBLICATIONS_ENDPOINT}/${encodedId}`);
      const companySummary = data?.company || data?.companySummary || {};
      return {
        companyId:
          data?.company_id ||
          data?.companyId ||
          companySummary?.id ||
          data?.company ||
          '',
        ownerId:
          companySummary?.owner_id ||
          companySummary?.ownerId ||
          data?.created_by ||
          data?.createdBy ||
          '',
        label: truncateText(
          data?.title || data?.unitName || data?.companyName || '',
          80
        ),
        status: data?.status || '',
      };
    }
  } catch (error) {
    if (error?.status !== 404) {
      console.warn('No se pudo obtener el contexto del objetivo reaccionado', kind, targetId, error);
    }
  }
  return null;
};

const buildReactionNotificationPayload = (kind, meta, actor) => {
  if (!meta || !actor?.id) return null;
  if (!meta.companyId) return null;
  if (meta.ownerId && meta.ownerId === actor.id) {
    return null;
  }

  const actorName =
    actor.displayName ||
    actor.name ||
    actor.username ||
    actor.email ||
    'Alguien';
  const baseUrl = '/dashboard?section=feed';

  if (kind === 'post') {
    const snippet = meta.label;
    const message = snippet
      ? `${actorName} reaccionó a tu actualizacion: "${snippet}".`
      : `${actorName} reaccionó a tu actualizacion en el feed.`;
    return {
      companyId: meta.companyId,
      type: 'reaction',
      title: 'Tu actualizacion recibió un like',
      message,
      destinationUrl: baseUrl,
      data: { kind, targetId: meta.id || '', label: snippet, actorId: actor.id },
      createdBy: actor.id,
    };
  }

  if (kind === 'request') {
    const snippet = meta.label;
    const message = snippet
      ? `${actorName} reaccionó a tu solicitud "${snippet}".`
      : `${actorName} reaccionó a tu solicitud en el marketplace.`;
    return {
      companyId: meta.companyId,
      type: 'reaction',
      title: 'Tu solicitud recibió un like',
      message,
      destinationUrl: baseUrl,
      data: { kind, targetId: meta.id || '', label: snippet, actorId: actor.id },
      createdBy: actor.id,
    };
  }

  if (kind === 'publication') {
    const snippet = meta.label;
    const message = snippet
      ? `${actorName} reaccionó a tu publicación "${snippet}".`
      : `${actorName} reaccionó a una de tus publicaciónes.`;
    return {
      companyId: meta.companyId,
      type: 'reaction',
      title: 'Tu publicación recibió un like',
      message,
      destinationUrl: baseUrl,
      data: { kind, targetId: meta.id || '', label: snippet, actorId: actor.id },
      createdBy: actor.id,
    };
  }

  return null;
};

const scheduleReactionNotification = async (kind, targetId, actorProfile) => {
  try {
    const actor = actorProfile || (await getSessionUserSnapshot());
    if (!actor?.id) return;
    const meta = await fetchReactionTargetMeta(kind, targetId);
    if (!meta) return;
    meta.id = targetId;
    const payload = buildReactionNotificationPayload(kind, meta, actor);
    if (!payload) return;
    await createNotification(payload);
  } catch (error) {
    console.warn('No se pudo registrar la notificacion de reacción', kind, targetId, error);
  }
};

export async function fetchReactionStats(kind, targetIds = []) {
  if (!Array.isArray(targetIds) || targetIds.length === 0) {
    return { counts: {}, userMap: {} };
  }

  const uniqueIds = Array.from(new Set(targetIds.filter(Boolean)));
  if (!uniqueIds.length) {
    return { counts: {}, userMap: {} };
  }

  try {
    const params = {
      kind,
      target_ids: uniqueIds.join(','),
    };
    const { data } = await http.get(`${FEED_REACTIONS_ENDPOINT}/stats`, { params });
    return {
      counts: data?.counts || {},
      userMap: data?.userMap || {},
    };
  } catch (error) {
    console.warn('No se pudieron obtener las estadísticas de reacciones', kind, error);
    return { counts: {}, userMap: {} };
  }
}

export async function toggleReaction(kind, targetId) {
  const actor = await getSessionUserSnapshot();
  if (!actor?.id) {
    const authError = new Error('AUTH_REQUIRED');
    authError.code = 'AUTH_REQUIRED';
    throw authError;
  }

  try {
    const { data } = await http.post(`${FEED_REACTIONS_ENDPOINT}/toggle`, {
      kind,
      target: targetId,
    });
    const reacted = Boolean(data?.reacted);
    const diff =
      typeof data?.diff === 'number'
        ? data.diff
        : reacted
        ? 1
        : -1;
    if (reacted) {
      scheduleReactionNotification(kind, targetId, actor);
    }
    return { reacted, diff };
  } catch (error) {
    if (error?.status === 401) {
      const authError = new Error('AUTH_REQUIRED');
      authError.code = 'AUTH_REQUIRED';
      throw authError;
    }
    console.warn('No se pudo alternar la reacción', kind, targetId, error);
    throw error;
  }
}
